import { useCallback, useEffect, useState } from "react";
import {
  createClientContact as defaultCreateContact,
  queryClientContacts as defaultLoadContacts,
  updateClientContact as defaultUpdateContact,
} from "../lib/clientDirectory";
import type {
  ClientContact,
  ClientRecord,
  CreateContactDraft,
} from "../types";
import { ContactForm } from "./ContactForm";
import { Modal } from "../../../components/Modal";
import styles from "./ClientContactsModal.module.css";

export interface ClientContactsModalProps {
  /** The client whose contacts are listed, created, and edited. */
  client: ClientRecord;
  onClose: () => void;
  /** Lists the client's contacts; defaults to the shared directory accessor. */
  loadContacts?: (clientId: string) => Promise<ClientContact[]>;
  /** Creates a contact under the client; defaults to the shared directory accessor. */
  createContact?: (clientId: string, draft: CreateContactDraft) => Promise<ClientContact>;
  /** Updates a contact under the client; defaults to the shared directory accessor. */
  updateContact?: (
    clientId: string,
    contactId: string,
    draft: CreateContactDraft,
  ) => Promise<ClientContact>;
}

/**
 * Manage one client's contacts on /clients.
 *
 * Lists the client's contacts and mints or edits them via the same inline
 * {@link ContactForm} the New Job form uses, so contact capture is identical on
 * both surfaces. All data access is pluggable; the component only orchestrates
 * load/append/replace/error state around it.
 *
 * Rendered through the shared {@link Modal}; the add-contact affordance sits in
 * the dialog footer so it stays reachable however long the list grows.
 */
export function ClientContactsModal({
  client,
  onClose,
  loadContacts = defaultLoadContacts,
  createContact = defaultCreateContact,
  updateContact = defaultUpdateContact,
}: ClientContactsModalProps) {
  const [contacts, setContacts] = useState<ClientContact[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  // Only one form (create or edit) is open at a time; edit takes precedence.
  const [editingContactId, setEditingContactId] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    try {
      setContacts(await loadContacts(client.id));
      setLoadError(null);
    } catch (loadFailure) {
      setLoadError(
        loadFailure instanceof Error ? loadFailure.message : "Could not load contacts",
      );
    }
  }, [client.id, loadContacts]);

  useEffect(() => {
    void load();
  }, [load]);

  const upsert = (contact: ClientContact): void => {
    setContacts((prev) => {
      if (!prev) return [contact];
      return prev.some((candidate) => candidate.id === contact.id)
        ? prev.map((candidate) => (candidate.id === contact.id ? contact : candidate))
        : [...prev, contact];
    });
  };

  const handleCreated = (contact: ClientContact): void => {
    upsert(contact);
    setCreating(false);
  };

  const handleUpdated = (contact: ClientContact): void => {
    upsert(contact);
    setEditingContactId(null);
  };

  const formOpen = creating || editingContactId !== null;

  return (
    <Modal
      title="Contacts"
      subtitle={client.name}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={styles.close} onClick={onClose}>
            Close
          </button>
          <button
            type="button"
            className={styles.add}
            disabled={formOpen || contacts === null}
            onClick={() => setCreating(true)}
          >
            Add contact
          </button>
        </>
      }
    >
      <div className={styles.body}>
        {loadError ? (
          <p className={styles.error} role="alert">
            {loadError}
          </p>
        ) : null}

        {contacts === null ? (
          <p className={styles.note}>Loading…</p>
        ) : (
          <>
            {contacts.length === 0 && !creating ? (
              <p className={styles.note}>No contacts yet.</p>
            ) : (
              <ul className={styles.list}>
                {contacts.map((contact) =>
                  contact.id === editingContactId ? (
                    <li key={contact.id} className={styles.editRow}>
                      <ContactForm
                        saveContact={(draft) => updateContact(client.id, contact.id, draft)}
                        initial={{
                          firstName: contact.firstName,
                          lastName: contact.lastName,
                          phone: contact.phone,
                          email: contact.email,
                          notes: contact.notes,
                        }}
                        heading={`Edit ${contact.displayName}`}
                        submitLabel="Save changes"
                        onSaved={handleUpdated}
                        onCancel={() => setEditingContactId(null)}
                      />
                    </li>
                  ) : (
                    <li key={contact.id} className={styles.row}>
                      <span className={styles.contactCell}>
                        <span className={styles.contactName}>{contact.displayName}</span>
                        {(() => {
                          const meta = [contact.email, contact.phone].filter(Boolean).join(" · ");
                          return meta ? (
                            <span className={styles.contactMeta}>{meta}</span>
                          ) : null;
                        })()}
                      </span>
                      <button
                        type="button"
                        className={styles.edit}
                        disabled={formOpen}
                        onClick={() => setEditingContactId(contact.id)}
                      >
                        Edit
                      </button>
                    </li>
                  ),
                )}
              </ul>
            )}

            {creating ? (
              <ContactForm
                saveContact={(draft) => createContact(client.id, draft)}
                onSaved={handleCreated}
                onCancel={() => setCreating(false)}
              />
            ) : null}
          </>
        )}
      </div>
    </Modal>
  );
}
